import React from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Code2, ArrowRight, MessageCircle, CheckCircle, Star, Award, Users, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const SEOLandingTemplate = ({ 
  title, 
  subtitle, 
  metaTitle, 
  metaDescription,
  heroImage,
  content,
  faqs,
  courseHighlights,
  keySkills
}) => {
  return (
    <div className="bg-black min-h-screen pt-14 sm:pt-16 md:pt-18">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-gradient-to-br from-gray-950 via-black to-gray-950">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(220,38,38,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(220,38,38,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 -left-32 w-96 h-96 bg-red-600 rounded-full filter blur-[128px] animate-pulse"></div>
            <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-orange-600 rounded-full filter blur-[128px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600/10 to-orange-600/10 border border-red-500/20 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
              <Shield className="w-5 h-5 text-red-500" />
              <span className="text-sm font-semibold text-gray-200 tracking-wide">Indian Cyber Club Academy - Jaipur</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              {title}
              <span className="block mt-3 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                {subtitle}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
              {metaDescription}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                size="lg"
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-10 py-7 shadow-xl shadow-red-500/30"
                onClick={() => window.open('https://indiancyberclub.in/checkout/c5ba72d4-c2ba-4a0b-8b5d-a7e55ba79aed', '_blank')}
              >
                Enroll Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white px-10 py-7"
                onClick={() => window.open('https://wa.me/919694612895', '_blank')}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Talk to Expert
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="text-2xl sm:text-3xl font-black text-red-500 mb-1">30K+</div>
                <div className="text-xs text-gray-400">Students Trained</div>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="text-2xl sm:text-3xl font-black text-red-500 mb-1">340K+</div>
                <div className="text-xs text-gray-400">Community</div>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="text-2xl sm:text-3xl font-black text-red-500 mb-1">95%</div>
                <div className="text-xs text-gray-400">Success Rate</div>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="text-2xl sm:text-3xl font-black text-red-500 mb-1">100%</div>
                <div className="text-xs text-gray-400">Practical</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 sm:py-24 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="prose prose-invert prose-lg max-w-none">
              {content.map((paragraph, index) => (
                <p key={index} className="text-gray-300 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights */}
      <section className="py-16 sm:py-24 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-12">
            What You'll <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Learn</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {courseHighlights.map((highlight, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-red-500/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{highlight.title}</h3>
                <p className="text-sm text-gray-400">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Skills */}
      <section className="py-16 sm:py-24 bg-gray-950">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-12">
            Skills You'll <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Master</span>
          </h2>
          <div className="flex flex-wrap gap-3 justify-center max-w-4xl mx-auto">
            {keySkills.map((skill, index) => (
              <div key={index} className="bg-gradient-to-br from-red-600/10 to-orange-600/10 border border-red-500/30 rounded-lg px-4 py-2">
                <span className="text-sm font-semibold text-gray-200">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-24 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-12">
            Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Questions</span>
          </h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">{faq.question}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-950 to-gray-900 border-2 border-red-500/30 rounded-3xl p-12 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
              Ready to Start Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                Cybersecurity Journey?
              </span>
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Join 30,000+ students who transformed their careers with Indian Cyber Club Academy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-10 py-7 shadow-xl"
                onClick={() => window.open('https://indiancyberclub.in/checkout/c5ba72d4-c2ba-4a0b-8b5d-a7e55ba79aed', '_blank')}
              >
                Enroll Now
              </Button>
              <Link to="/">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-10 py-7"
                >
                  View All Courses
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOLandingTemplate;
